import { useMemo, useRef } from "react";
import type { GameEvent, Player, Round } from "../api/types";
import { sideColor } from "../lib/view";
import { useCoarseTime, usePlayback } from "../player/store";

interface Props {
  round: Round;
  events: GameEvent[];
  players: Player[];
}

const MARKER_COLOR: Record<string, string> = {
  plant: "#ff3b3b",
  defuse: "#7ad4ff",
  detonate: "#ff8a3d",
  ultimate: "#c9a227",
};

/** Scrubbable timeline with a marker per notable event. */
export function Timeline({ round, events, players }: Props) {
  const barRef = useRef<HTMLDivElement>(null);
  const seek = usePlayback((s) => s.seek);
  const timeMs = useCoarseTime(50);

  const span = Math.max(1, round.endMs - round.startMs);
  const progress = ((timeMs - round.startMs) / span) * 100;

  const markers = useMemo(
    () =>
      events
        .filter((e) => e.kind !== "damage")
        .map((e) => {
          const actor = players.find((p) => p.pawn === (e.kind === "kill" ? e.target : e.actor));
          return {
            event: e,
            left: ((e.timeMs - round.startMs) / span) * 100,
            color:
              e.kind === "kill"
                ? actor
                  ? sideColor(actor, round)
                  : "#ffffff"
                : (MARKER_COLOR[e.kind] ?? "#ffffff"),
          };
        }),
    [events, players, round, span],
  );

  const seekTo = (clientX: number) => {
    const bar = barRef.current;
    if (!bar) return;
    const rect = bar.getBoundingClientRect();
    const fraction = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
    seek(round.startMs + fraction * span);
  };

  return (
    <div className="px-3 pb-2">
      <div
        ref={barRef}
        onPointerDown={(e) => {
          // Without this the browser starts selecting the surrounding text as
          // soon as the drag leaves the bar, and the selection then swallows
          // the pointer until it is cleared.
          e.preventDefault();
          e.currentTarget.setPointerCapture(e.pointerId);
          seekTo(e.clientX);
        }}
        onPointerMove={(e) => {
          if (e.buttons === 1) seekTo(e.clientX);
        }}
        className="relative h-9 cursor-pointer rounded bg-white/5"
      >
        {/* Buy phase, shaded: nothing happens there but it is part of the round. */}
        {round.liveMs !== undefined && (
          <div
            className="absolute inset-y-0 left-0 rounded-l bg-white/5"
            style={{ width: `${((round.liveMs - round.startMs) / span) * 100}%` }}
          />
        )}
        <div className="absolute inset-y-0 left-0 bg-white/10" style={{ width: `${progress}%` }} />

        {markers.map((m, i) => (
          <span
            key={i}
            title={describe(m.event, players)}
            onPointerDown={(e) => {
              e.stopPropagation();
              seek(m.event.timeMs);
            }}
            className="absolute top-1/2 h-3 w-[3px] -translate-y-1/2 rounded-sm"
            style={{ left: `${m.left}%`, background: m.color }}
          />
        ))}

        <div
          className="pointer-events-none absolute inset-y-0 w-[2px] bg-white"
          style={{ left: `${progress}%` }}
        />
      </div>
    </div>
  );
}

function describe(event: GameEvent, players: Player[]): string {
  const name = (pawn: number | undefined) =>
    players.find((p) => p.pawn === pawn)?.codename ?? "?";
  switch (event.kind) {
    case "kill":
      return `${name(event.actor)} élimine ${name(event.target)}${
        event.extra?.weapon ? ` (${event.extra.weapon})` : ""
      }`;
    case "plant":
      return "spike posé";
    case "defuse":
      return "spike désamorcé";
    case "detonate":
      return "spike explosé";
    case "ultimate":
      return `ultime de ${name(event.actor)}`;
    default:
      return event.kind;
  }
}
